import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import TextField from '../../FormElements/TextField/TextField';
import Button from '../../Button/Button';
import CountrySelect from '../../FormElements/CountrySelect/CountrySelect';
import { useSelectedServices } from '../ServicesSection/SelectedServicesContext';
import gsap from 'gsap';

import './formSection.css';


const Form = ({ scrollToServicesSection }, ref) => {
    const { selectedServices, removeService } = useSelectedServices();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        phoneCountryCode: '+31',
        phoneNumber: '',
        companyName: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const formRef = useRef(null);
    const successRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        if (isSubmitted) {
            gsap.to(formRef.current, {
                opacity: 0, duration: 0.3, onComplete: () => {
                    gsap.set(formRef.current, { display: 'none' });
                    gsap.set(successRef.current, { display: 'flex', opacity: 0 });
                    gsap.to(successRef.current, { opacity: 1, duration: 0.3 });
                }
            });
        } else {
            gsap.set(successRef.current, { display: 'none' });
            gsap.set(formRef.current, { display: 'flex', opacity: 0 });
            gsap.to(formRef.current, { opacity: 1, duration: 0.3 });
        }
    }, [isSubmitted]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        Object.keys(formData).forEach(key => {
            if (!formData[key].trim()) {
                newErrors[key] = '... vul je ook dit in?';
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            const notionData = {
                'Name': formData.name,
                'Email': formData.email,
                'Message': formData.message,
                'Phone': formData.phoneNumber,
                'Company': formData.companyName,
            };

            try {
                const response = await fetch('https://flintandsteel.io/notion-proxy-data.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(notionData),
                });

                if (response.ok) {
                    const result = await response.json();
                    setIsSubmitted(true);
                } else {
                    const error = await response.text();
                }
            } catch (error) {
            }
        }
    };

    return (
        <div className="form" ref={ref}>
            <div className="form-wrapper">
                <div className="form-text-wrapper">
                    <h2>
                        {location.pathname === '/' ? 'Hoe kunnen wij je helpen?' : 'Wil je meer weten?'}
                    </h2>
                    <p className="label">
                        {location.pathname === '/' ? 'Wij denken graag met je mee over:' : 'Stuur ons een berichtje!'}
                    </p>
                    {selectedServices.length > 0 && (
                    <div className="selected-services">
                        <div className="selected-services-list">
                            {selectedServices.map((service, index) => (
                                <div className="selected-service" key={index}>
                                    {service}
                                    <button
                                        className="remove-service-button"
                                        onClick={() => removeService(service)}
                                    >
                                        <span className="material-symbols-outlined">Close</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
                <div className="pre-form-wrapper">
                    <form className="form-input-wrapper" ref={formRef} onSubmit={handleSubmit}>
                        <TextField
                            label="Naam"
                            variant="outlined"
                            placeholder="Naam"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={errors.name}
                        />
                        <TextField
                            label="Bedrijfsnaam"
                            variant="outlined"
                            placeholder="Bedrijf"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            error={errors.companyName}
                        />
                        <TextField
                            label="E-mail"
                            variant="email"
                            placeholder="E-mailadres"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={errors.email}
                        />
                        <div className="phone-input grid grid--2-horizontal">
                            <CountrySelect
                                value={formData.phoneCountryCode}
                                onChange={(value) => handleInputChange({ target: { name: 'phoneCountryCode', value } })}
                                error={errors.phoneCountryCode}
                            />
                            <TextField
                                label="Telefoonnummer"
                                variant="outlined"
                                placeholder="Telefoonnummer"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                error={errors.phoneNumber}
                                onlyNumbers={true}
                            />
                        </div>
                        <TextField
                            label="Bericht"
                            variant="outlined"
                            placeholder="Bericht"
                            as="textarea"
                            rows={3}
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            error={errors.message}
                        />
                        <Button className="form-primary" variant="primary" onClick={handleSubmit}>Versturen</Button>
                        <p className=" offwhite">
                            Bij versturen ga je akkoord met onze <Button variant="textlink" to="/legal/legal">privacy statement.</Button>
                        </p>
                    </form>
                    <div ref={successRef} className="success-message" style={{ display: 'none' }}>
                        <p>Verstuurd! We nemen zo snel mogelijk contact met je op.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default forwardRef(Form);